import { flow, isEmpty } from 'lodash';
import { useMutation } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from 'react';
import cx from 'classnames';
import { RichTextViewer } from '@vimily/bonjoro-ui-components-v2/RichTextViewer';

import { useUploadVideoToS3 } from '~/App/Api/S3Upload';
import * as Actions from '~/App/Components/Record/Stores/Recorder/Actions';
import {
  getBlob,
  getRecorderState,
  getThumbnailBlob,
  getThumbnail,
} from '~/App/Components/Record/Stores/Recorder/Selectors';
import {
  getUploadUrl,
  UploadUrlParams,
  saveResponse,
  updateResponse,
  SaveResponseParams,
} from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';
import QuestionnaireCard, {
  QuestionSection,
  ResponseSection,
} from '~/App/Components/QuestionnaireCard';
import TestimonialContext from '../../Context/TestimonialContext';
import RespondentContext from '~/Share/TestimonialLandingPage/Context/Respondent';
import useStepContext from '~/App/Components/Typeform/useStepContext';
import {
  TestimonialResponse,
  TestimonialResponseType,
} from '~/types/models/testimonial';
import { ProceedButton } from '~/App/Components/QuestionnaireCard/QuestionSection';
import { QuestionResponseType } from '~/App/Components/QuestionnaireCard/ResponseSection';
import useTypeformNavContext from '~/Share/TestimonialLandingPage/Context/TypeformNav';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  TESTIMONIAL_RESPONSE_NEXT,
  TESTIMONIAL_RESPONSE_RECORD,
  TESTIMONIAL_RESPONSE_UPLOAD_SUCCESS,
  TESTIMONIAL_RESPONSE_UPLOAD,
  TESTIMONIAL_RESPONSE_TYPE,
} from '~/generated/AnalyticsTypes';

import { useTranslation } from 'react-i18next';
import { useFeatures } from '~/react-hooks/use-features';
import {
  Actions as TestimonialResponseActions,
  TestimonialResponseState,
  useTestimonialResponse,
} from '~/Share/TestimonialLandingPage/Context/TestimonialResponse';
import { DefaultUpdateOrCreateResponse } from '~/App/Api/rest';
import {
  getRichTextFromSlateData,
  parseRichTextToString,
  RichText,
  SlateData,
} from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { getTestimonialLogoUrl } from '../../util';
import {
  RecorderControl,
  RecorderState,
} from '@vimily/bonjoro-ui-components-v2/VideoRecorder';
import { useMedia } from 'react-use-media';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';

const testimonialResponseTracking = {
  [QuestionResponseType.Recording]: TESTIMONIAL_RESPONSE_RECORD,
  [QuestionResponseType.Upload]: TESTIMONIAL_RESPONSE_UPLOAD,
  [QuestionResponseType.Text]: TESTIMONIAL_RESPONSE_TYPE,
};

export interface QuestionnaireProps {
  questionId: string;
  hideQuestionHeader?: boolean;
  className?: string;
}

export default function Questionnaire({
  questionId,
  hideQuestionHeader = false,
  className = '',
}: QuestionnaireProps) {
  const [isLoading, setIsLoading] = useState(false);
  const analytics = useAnalytics();

  const recorderControl = useRef<RecorderControl>(null);
  const [recorderState, setRecorderState] = useState<RecorderState>(
    RecorderState.Loading
  );
  const [uploadOnCompleteButtonSource, setUploadOnCompleteButtonSource] =
    useState<ProceedButton | undefined>();
  const [uploadOnly, setUploadOnly] = useState(false);

  const testimonial = useContext(TestimonialContext);
  const { respondent, setRespondent } = useContext(RespondentContext);
  const { setNavBarProps } = useTypeformNavContext();

  const question = respondent.getQuestionByUuid(questionId);
  const response = question.getResponse();

  const [textResponse, setTextResponse] = useState<RichText | undefined>();
  const [videoResponseType, setVideoResponseType] = useState<
    QuestionResponseType.Recording | QuestionResponseType.Upload | undefined
  >();
  const { shouldHideBonjoroTestimonialBranding } = useFeatures();
  const isMobile = useMedia({
    maxWidth: 768,
  });

  const logoUrl = getTestimonialLogoUrl(
    shouldHideBonjoroTestimonialBranding,
    testimonial?.landing_page?.logo
  );

  const {
    font_family,
    text_color,
    cta_corner_radius,
    primary_color,
    secondary_color,
  } = testimonial?.metadata?.style ?? {};

  useEffect(() => {
    setTextResponse(response?.getTextResponse());
  }, [response]);

  const { nextStep, previousStep, isCurrent } = useStepContext();

  const dispatch = useDispatch();
  const videoBlob = useSelector(flow(getRecorderState, getBlob));
  const thumbnailSquareBlobPromise = useSelector(
    flow(getRecorderState, useCallback(getThumbnailBlob(), []))
  );
  const thumbnailFullBlobPromise = useSelector(
    flow(getRecorderState, useCallback(getThumbnailBlob('original'), []))
  );

  const selectedthumbnail = useSelector(
    flow(getRecorderState, useCallback(getThumbnail(), []))
  );

  const [manualUploadFile, setManualUploadFile] = useState<File>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadVideoToS3 = useUploadVideoToS3({
    onUploadProgress: setUploadProgress,
    currentProgress: 5,
    onUploadSuccess: () => dispatch(Actions.setBlob(null)),
  });

  const { dispatch: testimonialResponseDispatch } = useTestimonialResponse();

  const { mutateAsync: getUploadUrlMutation } = useMutation(
    (uploadUrlParams: UploadUrlParams) => getUploadUrl(uploadUrlParams)
  );

  const { mutateAsync: saveResponseMutation } = useMutation(
    (responseParam: SaveResponseParams) => saveResponse(responseParam)
  );

  const { mutateAsync: updateResponseMutation } = useMutation(
    (responseParam: SaveResponseParams) => updateResponse(responseParam)
  );

  /**
   * Cache busting. Video can be replaced with the same S3 URL.
   * There are some cases that even after reloading the page it still displays the old video.
   */
  const videoSourceUrl = useMemo(
    () => `${response?.get('video_url') as string}?t=${+new Date()}`,
    [response]
  );

  const hasChanges = useMemo(() => {
    const changedText = parseRichTextToString(textResponse ?? []);
    return (
      videoBlob ||
      (!isEmpty(changedText) &&
        parseRichTextToString(response?.getTextResponse() ?? []) !==
          changedText)
    );
  }, [videoBlob, textResponse, response, textResponse]);

  useEffect(() => {
    if (hasChanges) {
      testimonialResponseDispatch(
        TestimonialResponseActions.updateTestimonialResponseState({
          questionNumber: question.getNumber(),
          state: TestimonialResponseState.Changed,
        })
      );
    }
  }, [hasChanges]);
  /**
   * Reset states for uploading and remove loaded video
   */
  const reset = () => {
    setIsLoading(false);
    setUploadProgress(0);

    testimonialResponseDispatch(
      TestimonialResponseActions.updateTestimonialResponseState({
        questionNumber: question.getNumber(),
        state: TestimonialResponseState.Uploaded,
      })
    );
  };

  const { t } = useTranslation();

  const handleNextClick = useCallback(
    async (button: ProceedButton, uploadOnly: boolean = false) => {
      try {
        // Stops the recorder if it's currently recording and flag it to Upload on Complete
        if (recorderState === RecorderState.Recording) {
          recorderControl?.current?.stopRecording();
          setUploadOnCompleteButtonSource(button);
          setUploadOnly(uploadOnly);
          return;
        }

        if (hasChanges) {
          setIsLoading(true);
          testimonialResponseDispatch(
            TestimonialResponseActions.updateTestimonialResponseState({
              questionNumber: question.getNumber(),
              state: TestimonialResponseState.Uploading,
            })
          );

          const respondentId = respondent.get('uuid') as string;
          const questionId = question.get('uuid') as string;
          let params = {
            respondentId,
            questionId,
            responseId: response?.get('uuid') as string,
          } as SaveResponseParams;

          if (videoBlob) {
            const uploadUrl = await getUploadUrlMutation({
              respondentId,
              questionId,
            });

            await uploadVideoToS3(uploadUrl?.url, videoBlob);
            const thumbnailSquareBlob = await thumbnailSquareBlobPromise;
            const thumbnailFullBlob = await thumbnailFullBlobPromise;
            params = {
              ...params,
              type: TestimonialResponseType.Video,
              thumbnail: thumbnailSquareBlob ?? undefined,
              thumbnailFull: thumbnailFullBlob ?? undefined,
              timestamp: uploadUrl.timestamp,
            };

            analytics.track(TESTIMONIAL_RESPONSE_UPLOAD_SUCCESS, {
              context: videoResponseType,
            });
          } else {
            params = {
              ...params,
              type: TestimonialResponseType.Text,
              textResponse: textResponse,
            };
          }

          let responseResult: DefaultUpdateOrCreateResponse<TestimonialResponse>;
          if (params.responseId) {
            responseResult = await updateResponseMutation(params);
          } else {
            responseResult = await saveResponseMutation(params);
          }

          const newRespondent = respondent.setResponse(
            questionId,
            responseResult.data
          );
          setRespondent?.(newRespondent);

          reset();
        }

        if (!uploadOnly) {
          analytics.track(TESTIMONIAL_RESPONSE_NEXT, { context: button });
          nextStep();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reset();
      }
    },
    [
      question,
      videoBlob,
      textResponse,
      hasChanges,
      selectedthumbnail,
      recorderState,
      isCurrent,
    ]
  );

  /* Enables when upload on complete is triggered and if video recording is finished */
  useEffect(() => {
    if (
      !uploadOnCompleteButtonSource ||
      !videoBlob ||
      recorderState !== RecorderState.Complete
    )
      return;

    handleNextClick(uploadOnCompleteButtonSource, uploadOnly);
    setUploadOnCompleteButtonSource(undefined);
    setUploadOnly(false);
  }, [
    uploadOnCompleteButtonSource,
    uploadOnly,
    videoBlob,
    recorderState,
    handleNextClick,
  ]);

  /* Triggers the */
  useEffect(() => {
    if (!isCurrent && recorderState === RecorderState.Recording) {
      handleNextClick(ProceedButton.Scroll, true);
    }
  }, [isCurrent, recorderState, handleNextClick]);

  const handleBackClick = () => {
    dispatch(Actions.setIsExternalVideo(false));
    setRespondent?.(respondent.setResponse(questionId, undefined));
    reset();
  };

  useEffect(() => {
    if (!manualUploadFile || videoBlob == null || selectedthumbnail == '')
      return;
    handleNextClick(ProceedButton.ManualUpload, true);
  }, [manualUploadFile, videoBlob, selectedthumbnail]);

  const handleQuestionResponseTypeSelect = (type: QuestionResponseType) => {
    if (type !== QuestionResponseType.Text) {
      setVideoResponseType(type);
    }
    analytics.track(testimonialResponseTracking[type]);
  };

  useEffect(() => {
    if (!isCurrent) return;

    setNavBarProps(
      isLoading,
      !question.get('index'),
      () => handleNextClick(ProceedButton.Arrow),
      previousStep
    );
  }, [question, isLoading, isCurrent, handleNextClick]);

  /**
   * Call the handleNextClick method to auto upload response when the step changes.
   * isLoading - for checking if uploading is in progress.
   * isCurrent - for checking if the step changes.
   */
  useEffect(() => {
    if (!isLoading && !isCurrent) {
      handleNextClick(ProceedButton.Scroll, true);
    }
  }, [isLoading, isCurrent]);

  const questionBoxColor = question.get('bg_color') as string;
  const questionTextColor = computeTextColor(questionBoxColor);

  const heading = !hideQuestionHeader ? (
    <h4
      className='text-xl font-medium mt-0 mb-5 text-center'
      style={{ color: questionTextColor, fontFamily: font_family }}
    >
      {t('testimonial:question.header')} {question.getNumber()}
    </h4>
  ) : null;

  const questionComponent = (
    <RichTextViewer
      value={getRichTextFromSlateData(
        question.get('question_rich') as SlateData[]
      )}
      style={{
        color: questionTextColor,
        fontFamily: font_family,
      }}
    />
  );

  return (
    <div className='overflow-x-auto flex justify-center relative w-full my-auto h-full'>
      <QuestionnaireCard
        bgColor={questionBoxColor}
        className={cx('bg-yellow-100 m-auto', className)}
      >
        <div className='md:!hidden mt-4 xs:mt-8 w-full flex justify-center'>
          {logoUrl && (
            <img
              src={logoUrl}
              width={125}
              height={25}
              className='object-contain'
            />
          )}
        </div>
        <QuestionSection
          heading={heading}
          question={questionComponent}
          onNextClick={(button) => void handleNextClick(button)}
          onPreviousClick={() => previousStep()}
          previousDisabled={!question.get('index')}
          nextDisabled={isLoading}
          ctaColor={primary_color}
          ctaCornerRadius={cta_corner_radius}
          fontFamily={font_family}
        />
        <ResponseSection
          recorderControlRef={recorderControl}
          className='md:max-w-[50%]'
          heading={heading}
          question={questionComponent}
          textResponse={response?.getTextResponse()}
          acceptedResponseTypes={question.getResponseType()}
          onTextResponseChange={setTextResponse}
          uploading={isLoading}
          uploadProgress={uploadProgress}
          videoSource={videoSourceUrl}
          updatedAt={response?.get('updated_at') as string | undefined}
          onQuestionResponseTypeSelect={handleQuestionResponseTypeSelect}
          responseType={
            response?.get('type') as TestimonialResponseType | undefined
          }
          onBackClick={handleBackClick}
          bgColor={questionBoxColor}
          testimonialUuid={testimonial.uuid}
          manualUploadFile={manualUploadFile}
          onManualUploadFileSelect={setManualUploadFile}
          responseUuid={response?.get('uuid') as string}
          recorderState={recorderState}
          onRecorderStateChange={setRecorderState}
          primaryCtaColor={primary_color}
          secondaryCtaColor={secondary_color}
          ctaCornerRadius={cta_corner_radius}
          fontColor={isMobile ? questionTextColor : text_color}
          fontFamily={font_family}
        />
      </QuestionnaireCard>
    </div>
  );
}
